import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { Plug, RefreshCw, Unplug } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { z } from 'zod';
import { QueryKey } from '@/Config/api/queryKey';
import { IntegrationEnabledBadge } from '@/Domain/components/Integration/IntegrationEnabledBadge';
import { IntegrationStatusBadge } from '@/Domain/components/Integration/IntegrationStatusBadge';
import { IntegrationConnectDialog } from '@/Domain/components/Integration/components/IntegrationConnectDialog';
import { IntegrationConnectionCard } from '@/Domain/components/Integration/components/IntegrationConnectionCard';
import { IntegrationIntroduction } from '@/Domain/components/Integration/components/IntegrationIntroduction';
import { IntegrationSection } from '@/Domain/components/Integration/components/IntegrationSection';
import { IntegrationToggleField } from '@/Domain/components/Integration/components/IntegrationToggleField';
import { Button, IconButton } from '@/Domain/components/Shared/Button/Button';
import { Input } from '@/Domain/components/Shared/Input/Input';
import { Label } from '@/Domain/components/Shared/Label/Label';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/Domain/components/Shared/Sheet/sheet';
import { Spinner } from '@/Domain/components/Shared/Spinner';
import CloseIcon from '@/Domain/components/icons/Close.icon';
import { TelegramIcon } from '@/Domain/components/icons/Telegram.icon';
import { PostHogEventEnum } from '@/Domain/enum/PostHogEvent.enum';
import { usePostHogAnalytics } from '@/Domain/hooks/App/usePostHog/usePostHogAnalytics.hook';
import {
  trackIntegrationConnectFailed,
  trackIntegrationConnectStarted,
  trackIntegrationConnectSucceeded,
} from '@/Domain/hooks/Integration/integrationConnectAnalytics';

const TELEGRAM_INTEGRATION_NAME = 'telegram' as const;
const TELEGRAM_QUERY_KEY = [QueryKey.integration.get, TELEGRAM_INTEGRATION_NAME] as const;
const TELEGRAM_DOCS_URL = 'https://docs.quickchat.ai/channels/telegram';
const BOTFATHER_URL = 'https://t.me/botfather';
const LINK_CLASS =
  'text-foreground link-underline underline-offset-2 transition-colors hover:text-muted-foreground';

interface TelegramIntegrationParams {
  telegram_bot_active: boolean;
  telegram_bot_token: string;
}

interface TelegramIntegrationResponse {
  name: typeof TELEGRAM_INTEGRATION_NAME;
  params: TelegramIntegrationParams;
}

const telegramFormSchema = z.object({
  telegram_bot_active: z.boolean(),
  telegram_bot_token: z.string(),
});

type TelegramFormValues = z.infer<typeof telegramFormSchema>;

interface TelegramSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const TelegramSheet = ({ open, onOpenChange }: TelegramSheetProps) => {
  const { t } = useTranslation(['integration']);
  const queryClient = useQueryClient();
  const { capture } = usePostHogAnalytics();
  const [connectDialogOpen, setConnectDialogOpen] = useState(false);

  const { data, isSuccess } = useQuery<TelegramIntegrationResponse>({
    queryKey: TELEGRAM_QUERY_KEY,
    enabled: open,
  });

  const params = data?.params;

  // Bot name + username are fetched live (never persisted) each time the sheet
  // opens while connected, so they always reflect the current Telegram profile.
  const { data: botInfo, isFetching: botInfoLoading } = useQuery<{
    name: string;
    username: string;
  }>({
    queryKey: [QueryKey.integration.telegramBotInfo],
    enabled: open && Boolean(params?.telegram_bot_token),
  });

  // Prefilled with the saved token, mirroring the Discord sheet. Clearing the
  // field and saving removes the token.
  const initialValues = useMemo<TelegramFormValues>(
    () => ({
      telegram_bot_active: params?.telegram_bot_active ?? false,
      telegram_bot_token: params?.telegram_bot_token ?? '',
    }),
    [params],
  );

  const form = useForm<TelegramFormValues>({
    resolver: zodResolver(telegramFormSchema),
    defaultValues: initialValues,
    values: initialValues,
  });

  useEffect(() => {
    if (open) {
      capture(PostHogEventEnum.TelegramIntegrationSheetOpened);
    }
  }, [open, capture]);

  // Two stages, mirroring the OAuth sheets: Connected = authenticated (a token is
  // saved); Enabled = the bot's webhook is currently on (the active toggle).
  const isConnected = Boolean(params?.telegram_bot_token);
  const isEnabled = Boolean(params?.telegram_bot_active);
  // The token is `<bot_id>:<secret>`; the bot id prefix is public, the rest secret.
  const botId = (params?.telegram_bot_token ?? '').split(':')[0];
  const botName = botInfo?.name ?? '';
  const botUsername = botInfo?.username ?? '';

  const mutation = useMutation<
    TelegramIntegrationResponse,
    AxiosError<{ detail?: string }>,
    { name: typeof TELEGRAM_INTEGRATION_NAME; params: Partial<TelegramIntegrationParams> }
  >({
    mutationKey: [QueryKey.integration.patch],
    onSuccess: (response) => {
      queryClient.setQueryData(TELEGRAM_QUERY_KEY, (prev?: TelegramIntegrationResponse) => ({
        name: TELEGRAM_INTEGRATION_NAME,
        params: {
          ...(prev?.params ?? { telegram_bot_active: false, telegram_bot_token: '' }),
          ...response.params,
        },
      }));
      toast.success(t('integration:telegram.saved'));
    },
    onError: (error) => {
      // Surface the backend `detail` when present; never log the token itself.
      const detail = error.response?.data?.detail;
      capture(PostHogEventEnum.TelegramIntegrationSaveFailed, { reason: detail ?? 'unknown' });
      toast.error(detail ?? t('integration:telegram.save_failed'));
    },
  });

  // Dedicated mutation for the enable/disable toggle so it shows a standardized
  // "<name> integration enabled/disabled" toast (never "saved") and never closes
  // the sheet, unlike Connect/Disconnect which reuse `mutation`.
  const toggleMutation = useMutation<
    TelegramIntegrationResponse,
    AxiosError<{ detail?: string }>,
    { name: typeof TELEGRAM_INTEGRATION_NAME; params: Partial<TelegramIntegrationParams> }
  >({
    mutationKey: [QueryKey.integration.patch],
    onSuccess: (response, variables) => {
      queryClient.setQueryData(TELEGRAM_QUERY_KEY, (prev?: TelegramIntegrationResponse) => ({
        name: TELEGRAM_INTEGRATION_NAME,
        params: {
          ...(prev?.params ?? { telegram_bot_active: false, telegram_bot_token: '' }),
          ...response.params,
        },
      }));
      const name = t('integration:telegram.title');
      toast.success(
        variables.params.telegram_bot_active
          ? t('integrations:toast.enabled', { name })
          : t('integrations:toast.disabled', { name }),
      );
    },
    onError: (error) => {
      const detail = error.response?.data?.detail;
      toast.error(detail ?? t('integration:telegram.save_failed'));
    },
  });

  const tokenValue = form.watch('telegram_bot_token');

  // Connect = authenticate by saving the token; like the OAuth sheets, connecting
  // also turns the bot on.
  const handleConnect = form.handleSubmit((values) => {
    const trimmedToken = values.telegram_bot_token.trim();
    if (!trimmedToken) return;
    trackIntegrationConnectStarted(capture, 'telegram');
    mutation.mutate(
      {
        name: TELEGRAM_INTEGRATION_NAME,
        params: { telegram_bot_token: trimmedToken, telegram_bot_active: true },
      },
      {
        onSuccess: () => {
          setConnectDialogOpen(false);
          capture(PostHogEventEnum.TelegramIntegrationSaved, {
            bot_active: true,
            token_changed: trimmedToken !== (params?.telegram_bot_token ?? ''),
            token_removed: false,
          });
          trackIntegrationConnectSucceeded(capture, 'telegram');
        },
        onError: () => {
          // Bounded reason for the unified funnel; the raw backend detail stays
          // on the telegram_integration_save_failed event (mutation-level onError).
          trackIntegrationConnectFailed(capture, 'telegram', 'save_failed');
        },
      },
    );
  });

  // Disconnect = remove the credential (de-authenticate); also turns the bot off.
  const handleDisconnect = () => {
    mutation.mutate({
      name: TELEGRAM_INTEGRATION_NAME,
      params: { telegram_bot_token: '', telegram_bot_active: false },
    });
  };

  // Enable/disable toggle = pause/resume the webhook without losing the token.
  const handleToggleActive = (checked: boolean) => {
    toggleMutation.mutate({
      name: TELEGRAM_INTEGRATION_NAME,
      params: { telegram_bot_active: checked },
    });
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        className="max-w-xl w-full h-full pb-0 space-y-0"
        data-testid="telegram-sheet"
        onEscapeKeyDown={(e) => {
          e.preventDefault();
          onOpenChange(false);
        }}
      >
        <SheetHeader className="px-0.5 items-center flex-row">
          <div className="flex items-center gap-3">
            <TelegramIcon className="size-8 shrink-0" />
            <div className="flex flex-col gap-1">
              <div className="flex items-center gap-2">
                <SheetTitle data-testid="telegram-sheet-title">
                  {t('integration:telegram.title')}
                </SheetTitle>
                <IntegrationStatusBadge connected={isConnected} />
                <IntegrationEnabledBadge enabled={isEnabled} />
              </div>
            </div>
          </div>
          <IconButton
            onClick={() => onOpenChange(false)}
            variant="secondary"
            data-testid="telegram-close-button"
            className="shrink-0"
          >
            <CloseIcon />
          </IconButton>
        </SheetHeader>

        <div className="overflow-y-auto px-0.5 pb-6 space-y-6">
          <IntegrationIntroduction
            description={t('integration:telegram.overview')}
            prerequisites={(
              t('integration:telegram.prerequisites', { returnObjects: true }) as string[]
            ).map((_, index) => (
              <Trans
                key={index}
                t={t}
                i18nKey={`integration:telegram.prerequisites.${index}`}
                components={[
                  <a
                    key="botfather"
                    href={BOTFATHER_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={LINK_CLASS}
                  />,
                ]}
              />
            ))}
            docsHref={TELEGRAM_DOCS_URL}
          />

          {!isSuccess ? (
            <div className="flex items-center justify-center py-12" data-testid="telegram-loading">
              <Spinner className="size-5 text-muted-foreground" />
            </div>
          ) : (
            <>
              <IntegrationSection
                title={t('integrations:section.connect')}
                data-testid="telegram-connect-enable-section"
              >
                {isConnected ? (
                  <IntegrationConnectionCard
                    data-testid="telegram-connection-card"
                    details={[
                      {
                        label: t('integration:telegram.bot_name'),
                        value: botInfoLoading ? (
                          <Spinner
                            className="size-3.5 text-foreground-subtle"
                            data-testid="telegram-bot-name-loader"
                          />
                        ) : (
                          botName
                        ),
                        'data-testid': 'telegram-bot-name',
                      },
                      {
                        label: t('integration:telegram.bot_username'),
                        value: botInfoLoading ? (
                          <Spinner
                            className="size-3.5 text-foreground-subtle"
                            data-testid="telegram-bot-username-loader"
                          />
                        ) : botUsername ? (
                          `@${botUsername}`
                        ) : (
                          ''
                        ),
                        'data-testid': 'telegram-bot-username',
                      },
                      {
                        label: t('integration:telegram.bot_id'),
                        value: botId,
                        'data-testid': 'telegram-bot-id',
                      },
                    ]}
                    actions={
                      <>
                        <Button
                          type="button"
                          variant="secondary"
                          size="sm"
                          className="flex-1"
                          onClick={() => setConnectDialogOpen(true)}
                          data-testid="telegram-reconnect-button"
                        >
                          <RefreshCw className="size-4" />
                          {t('integrations:reconnect')}
                        </Button>
                        <Button
                          type="button"
                          variant="danger"
                          size="sm"
                          className="flex-1"
                          onClick={handleDisconnect}
                          disabled={mutation.isPending}
                          data-testid="telegram-disconnect-button"
                        >
                          <Unplug className="size-4" />
                          {t('integrations:disconnect')}
                        </Button>
                      </>
                    }
                  />
                ) : (
                  <Button
                    type="button"
                    variant="dark"
                    size="sm"
                    className="w-full"
                    onClick={() => setConnectDialogOpen(true)}
                    data-testid="telegram-connect-button"
                  >
                    <Plug className="size-4" />
                    {t('integrations:connect', { name: 'Telegram' })}
                  </Button>
                )}
              </IntegrationSection>

              <IntegrationSection
                title={t('integrations:section.settings')}
                data-testid="telegram-settings-section"
              >
                <IntegrationToggleField
                  id="telegram-enable-switch"
                  label={t('integrations:enable', { name: 'Telegram' })}
                  checked={isEnabled}
                  onCheckedChange={handleToggleActive}
                  disabled={!isConnected || toggleMutation.isPending}
                  data-testid="telegram-enable-switch"
                />
              </IntegrationSection>
            </>
          )}
        </div>

        <IntegrationConnectDialog
          open={connectDialogOpen}
          onOpenChange={setConnectDialogOpen}
          title={t('integrations:connect', { name: 'Telegram' })}
          description={
            <Trans
              t={t}
              i18nKey="integration:telegram.connect_description"
              components={[
                <a
                  key="botfather"
                  href={BOTFATHER_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={LINK_CLASS}
                />,
              ]}
            />
          }
          submitLabel={t('integrations:connect', { name: 'Telegram' })}
          submitIcon={<Plug className="size-4" />}
          onSubmit={handleConnect}
          submitDisabled={!tokenValue?.trim() || mutation.isPending}
          isSubmitting={mutation.isPending}
          data-testid="telegram-connect-dialog"
        >
          <Controller
            control={form.control}
            name="telegram_bot_token"
            render={({ field }) => (
              <div className="space-y-1.5">
                <Label htmlFor="telegram-bot-token-input">
                  {t('integration:telegram.bot_token.label')}
                </Label>
                <Input
                  id="telegram-bot-token-input"
                  autoComplete="off"
                  data-1p-ignore
                  data-lpignore="true"
                  autoCorrect="off"
                  autoCapitalize="off"
                  spellCheck={false}
                  placeholder={t('integration:telegram.bot_token.placeholder')}
                  value={field.value}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  data-testid="telegram-bot-token-input"
                  className="w-full"
                />
              </div>
            )}
          />
        </IntegrationConnectDialog>
      </SheetContent>
    </Sheet>
  );
};
